import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getAmbientesControllerListarQueryKey,
  useAmbientesControllerAtualizar,
  useAmbientesControllerCriar,
} from '@/api/generated/admin-ambientes/admin-ambientes';
import type { AmbienteResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';

const schema = z.object({
  nome: z.string().trim().min(3, 'Informe um nome com ao menos 3 caracteres').max(120),
  tipo: z.enum(['INDIVIDUAL', 'PARCEIRO']),
  /**
   * ⚠️ `string`, e NÃO `z.coerce.number()`. Campo vazio é `''`, e o `coerce` o
   * transformaria em `0` — apontando o ambiente silenciosamente para a
   * "clínica 0" em vez de limpar o vínculo. A conversão para `number | null`
   * acontece no submit, onde vazio tem o significado certo.
   */
  codClinicaCrm: z
    .string()
    .trim()
    .regex(/^\d*$/, 'Use apenas números')
    .max(12),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  ambiente?: AmbienteResponse;
}

/**
 * ⚠️ Na edição, só o nome é enviado.
 *
 * `tipo` é definido na criação e não muda: trocar INDIVIDUAL → PARCEIRO mudaria
 * o significado de todo dado já pendurado no ambiente, e a volta deixaria o
 * perfil de parceiro órfão. O servidor recusa com 422 — aqui o campo fica
 * desabilitado para o usuário não descobrir isso depois de digitar.
 */
export function AmbienteFormDialogo({ aberto, aoFechar, ambiente }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(ambiente);

  const criar = useAmbientesControllerCriar();
  const atualizar = useAmbientesControllerAtualizar();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (aberto) {
      reset({
        nome: ambiente?.nome ?? '',
        tipo: ambiente?.tipo ?? 'INDIVIDUAL',
        codClinicaCrm: ambiente?.codClinicaCrm?.toString() ?? '',
      });
    }
  }, [aberto, ambiente, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      if (ambiente) {
        await atualizar.mutateAsync({
          id: ambiente.id,
          data: {
            nome: dados.nome,
            // Vazio manda `null` EXPLÍCITO: é o que devolve o ambiente à busca
            // automática por CRMV. Omitir o campo manteria o vínculo antigo, e
            // o admin não teria como desfazer o que digitou.
            codClinicaCrm: dados.codClinicaCrm ? Number(dados.codClinicaCrm) : null,
          },
        });
      } else {
        await criar.mutateAsync({ data: { nome: dados.nome, tipo: dados.tipo } });
      }

      await queryClient.invalidateQueries({ queryKey: getAmbientesControllerListarQueryKey() });
      toast.success(editando ? 'Ambiente atualizado.' : 'Ambiente criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o ambiente.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar ambiente' : 'Novo ambiente'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'O identificador (slug) não muda ao renomear — ele já pode estar em links e registros.'
              : 'O tipo é definido agora e não pode ser alterado depois.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="nome" rotulo="Nome" erro={errors.nome?.message} obrigatorio>
            <Input
              id="nome"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: Clínica Vida Animal"
              {...register('nome')}
            />
          </Campo>

          <Campo
            id="tipo"
            rotulo="Tipo"
            erro={errors.tipo?.message}
            obrigatorio
            dica={
              editando
                ? 'O tipo não pode ser alterado depois da criação.'
                : 'Parceiro é para empresas do Clube; individual é para clínicas e veterinários.'
            }
          >
            <Controller
              control={control}
              name="tipo"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={editando}>
                  <SelectTrigger id="tipo" className={CLASSE_CONTROLE}>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                    <SelectItem value="PARCEIRO">Parceiro</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Campo>

          {/* Só na edição: um ambiente recém-criado ainda não tem médico
              cadastrado, e o vínculo com o CRM é uma correção de algo que já
              existe, não parte da abertura. */}
          {editando && (
            <Campo
              id="codClinicaCrm"
              rotulo="Código da clínica no CRM"
              erro={errors.codClinicaCrm?.message}
              dica="Opcional. Preenchido, substitui a busca por CRMV no nível do Clube. Vazio volta ao automático."
            >
              <Input
                id="codClinicaCrm"
                inputMode="numeric"
                className={CLASSE_CONTROLE}
                placeholder="Ex.: 90210"
                {...register('codClinicaCrm')}
              />
            </Campo>
          )}

          {editando && ambiente && (
            <p className="text-xs text-muted-foreground">
              Identificador: <code className="text-brand">{ambiente.slug}</code>
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar ambiente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
